import React, { useState, useEffect } from 'react';
import { Card, Tabs, Button, message, Spin, Row, Col, Statistic } from 'antd';
import { 
  CrownOutlined, 
  CarOutlined, 
  UserOutlined, 
  CalendarOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import VIPList from './components/VIPList';
import VIPForm from './components/VIPForm';
import ExpiringVIPs from './components/ExpiringVIPs';
import { vipService, VIPParking } from '../../services/vipService';
import './VIPManagement.css';

const { TabPane } = Tabs;

interface VIPStats {
  totalVIPs: number;
  activeVIPs: number;
  expiredVIPs: number;
  suspendedVIPs: number;
  platinumCount: number;
  goldCount: number;
  silverCount: number;
  expiringCount: number;
}

const VIPManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<VIPStats>({
    totalVIPs: 0,
    activeVIPs: 0,
    expiredVIPs: 0,
    suspendedVIPs: 0,
    platinumCount: 0,
    goldCount: 0,
    silverCount: 0,
    expiringCount: 0,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    loadStats();
  }, [refreshKey]);

  const loadStats = async () => {
    try {
      setLoading(true);
      
      // 並行載入各種統計數據
      const [
        allVIPs,
        activeVIPs,
        expiredVIPs,
        suspendedVIPs,
        platinumVIPs,
        goldVIPs,
        silverVIPs,
        expiringVIPs
      ] = await Promise.all([
        vipService.getVIPParkings({ limit: 1000 }),
        vipService.getVIPParkings({ status: 'active', limit: 1000 }),
        vipService.getVIPParkings({ status: 'expired', limit: 1000 }),
        vipService.getVIPParkings({ status: 'suspended', limit: 1000 }),
        vipService.getVIPParkings({ vipLevel: 'platinum', limit: 1000 }),
        vipService.getVIPParkings({ vipLevel: 'gold', limit: 1000 }),
        vipService.getVIPParkings({ vipLevel: 'silver', limit: 1000 }),
        vipService.getExpiringVIPs(7)
      ]);

      setStats({
        totalVIPs: allVIPs.pagination.totalRecords,
        activeVIPs: activeVIPs.pagination.totalRecords,
        expiredVIPs: expiredVIPs.pagination.totalRecords,
        suspendedVIPs: suspendedVIPs.pagination.totalRecords,
        platinumCount: platinumVIPs.pagination.totalRecords,
        goldCount: goldVIPs.pagination.totalRecords,
        silverCount: silverVIPs.pagination.totalRecords,
        expiringCount: expiringVIPs.count,
      });
    } catch (error) {
      console.error('載入VIP統計數據失敗:', error);
      message.error('載入VIP統計數據失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleDataChange = () => {
    // 當數據發生變化時刷新統計
    handleRefresh();
  };

  return (
    <div className="vip-management">
      {/* 頁面標題和操作 */}
      <div className="header-section">
        <div className="header-content">
          <div className="title-section">
            <h1>
              <CrownOutlined className="title-icon" />
              VIP車位管理
            </h1>
            <p>管理VIP會員車位分配、權限設定和到期提醒</p>
          </div>
          <div className="action-section">
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              刷新數據
            </Button>
          </div>
        </div>
      </div>

      {/* 統計概覽 */}
      <div className="overview-section">
        <Row gutter={[16, 16]} className="overview-cards">
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card className="stat-card total-card">
              <div className="stat-content">
                <div className="stat-icon">
                  <CrownOutlined />
                </div>
                <div className="stat-info">
                  <Statistic
                    title="總VIP數量"
                    value={stats.totalVIPs}
                    loading={loading}
                  />
                  <div className="stat-detail">全部會員</div>
                </div>
              </div>
            </Card>
          </Col>
          
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card className="stat-card active-card">
              <div className="stat-content">
                <div className="stat-icon">
                  <CheckCircleOutlined />
                </div>
                <div className="stat-info">
                  <Statistic
                    title="啟用中"
                    value={stats.activeVIPs}
                    loading={loading}
                  />
                  <div className="stat-detail">正常使用</div>
                </div>
              </div>
            </Card>
          </Col>
          
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card className="stat-card warning-card">
              <div className="stat-content">
                <div className="stat-icon">
                  <ExclamationCircleOutlined />
                </div>
                <div className="stat-info">
                  <Statistic
                    title="即將過期"
                    value={stats.expiringCount}
                    loading={loading}
                  />
                  <div className="stat-detail">7天內到期</div>
                </div>
              </div>
            </Card>
          </Col>
          
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card className="stat-card expired-card">
              <div className="stat-content">
                <div className="stat-icon">
                  <CloseCircleOutlined />
                </div>
                <div className="stat-info">
                  <Statistic
                    title="已過期"
                    value={stats.expiredVIPs}
                    loading={loading}
                  />
                  <div className="stat-detail">需要續期</div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* VIP等級分布 */}
        <Row gutter={[16, 16]} className="level-cards" style={{ marginTop: 16 }}>
          <Col xs={8} sm={8} md={8} lg={8}>
            <Card className="stat-card platinum-card">
              <div className="stat-content">
                <div className="stat-info">
                  <Statistic
                    title="白金會員"
                    value={stats.platinumCount}
                    loading={loading}
                  />
                  <div className="stat-detail">Platinum</div>
                </div>
              </div>
            </Card>
          </Col>
          
          <Col xs={8} sm={8} md={8} lg={8}>
            <Card className="stat-card gold-card">
              <div className="stat-content">
                <div className="stat-info">
                  <Statistic
                    title="黃金會員"
                    value={stats.goldCount}
                    loading={loading}
                  />
                  <div className="stat-detail">Gold</div>
                </div>
              </div>
            </Card>
          </Col>
          
          <Col xs={8} sm={8} md={8} lg={8}>
            <Card className="stat-card silver-card">
              <div className="stat-content">
                <div className="stat-info">
                  <Statistic
                    title="銀牌會員"
                    value={stats.silverCount}
                    loading={loading}
                  />
                  <div className="stat-detail">Silver</div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* 管理功能標籤頁 */}
      <Card className="management-tabs-card">
        <Tabs defaultActiveKey="list" size="large">
          <TabPane
            tab={
              <span>
                <CarOutlined />
                VIP車位列表
              </span>
            }
            key="list"
          >
            <VIPList 
              refreshKey={refreshKey}
              onDataChange={handleDataChange}
            />
          </TabPane>
          
          <TabPane
            tab={
              <span>
                <UserOutlined />
                新增VIP
              </span>
            }
            key="add"
          >
            <VIPForm 
              onSuccess={handleDataChange}
            />
          </TabPane>
          
          <TabPane
            tab={
              <span>
                <CalendarOutlined />
                到期提醒
              </span>
            }
            key="expiring"
          >
            <ExpiringVIPs 
              refreshKey={refreshKey}
              onDataChange={handleDataChange}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default VIPManagement;